/** @format */

import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadIcon from "@mui/icons-material/Download";
import SaveIcon from "@mui/icons-material/Save";
import SendIcon from "@mui/icons-material/Send";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { toast } from "react-toastify";
import moment from "moment";
import CapexInputForm from "./capex-input-form";
import CapexOutput from "./capex-output";
import { downloadCapexExport } from "./capex-api";
import { defaultCapexAnswers, stripHiddenAnswers, validateCapexInputs } from "./capex-validation";
import { invokeApi, HTTP_METHODS } from "../../utils/http-service";
import { getOfflineData } from "../../utils/offline-services";
import { CF_APPROVAL_HOSTNAME } from "../../../common-utilities/utils/end-points";
import { REST_URLS } from "../../../common-utilities/utils/end-points/cf-approval";
import { CF_APPROVAL_COMMON_ROUTE_V2 } from "../../../cf-approval/utils/constants";

const NO_PERMISSION_MESSAGE = "You do not have permission to perform this action";

// The API contract's POST /calculate body is { cfId, storeFormat, inputAnswers }, with
// storeFormat as a sibling of inputAnswers -- but STORE_FORMAT is itself the Input Form's own
// first field (Category: "Store setup", per Input Options Master), so its value is read straight
// out of `answers.STORE_FORMAT` rather than collected separately.
const CapexCalculatorPage = () => {
  const { cfId } = useParams();
  const navigate = useNavigate();

  const [fields, setFields] = useState([]);
  const [fieldsLoading, setFieldsLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  const [outputLines, setOutputLines] = useState([]);
  const [calculatedAt, setCalculatedAt] = useState(null);
  const [resultLoading, setResultLoading] = useState(true);
  const [showErrors, setShowErrors] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [sendingCapex, setSendingCapex] = useState(false);

  const currentUser = getOfflineData("user");
  const allStepsMaster = useSelector((state) => state.cfApprovalV2.stepMaster) || [];

  // Access is granted to BD_LEAD/BD_REGIONAL_HEAD roles, plus whichever specific user is
  // configured as Step 18's approver in the step master (matched by user id, not role).
  const step18Master = allStepsMaster.find((s) => s.stepNumber === 18);
  const step18UserIds = step18Master
    ? step18Master.approverType === "REGIONAL_USER"
      ? (step18Master.regionalApprovers || []).map((ra) => ra.approverUserId)
      : [step18Master.approverUserId, ...(step18Master.approverUserIds || [])].filter((id) => id != null)
    : [];
  const isStep18User = step18UserIds.map(Number).includes(+currentUser?.id);
  const canUseCapexActions =
    ["BD_LEAD", "BD_REGIONAL_HEAD"].includes(currentUser?.role?.toUpperCase()) || isStep18User;

  // calculatedAt is set both by the initial GET /:cfId fetch (a prior saved calculation) and by
  // runCalculation's success handler (a fresh save) -- so it doubles as "has this CF's Capex ever
  // been saved" without any extra state.
  const canSendCapex = Boolean(calculatedAt);

  useEffect(() => {
    setFieldsLoading(true);
    invokeApi(HTTP_METHODS.GET, `${CF_APPROVAL_HOSTNAME}${REST_URLS.CAPEX_INPUT_FORM}`)
      .then((response) => {
        const fieldList = Array.isArray(response) ? response : response?.results || [];
        setFields(fieldList);
      })
      .catch(() => toast.error("Failed to load Capex Calculator input form"))
      .finally(() => setFieldsLoading(false));

    setResultLoading(true);
    invokeApi(HTTP_METHODS.GET, `${CF_APPROVAL_HOSTNAME}${REST_URLS.CAPEX_RESULT}${cfId}`)
      .then((response) => {
        setAnswers(response?.inputAnswers || {});
        setOutputLines(response?.outputLines || []);
        setCalculatedAt(response?.calculatedAt || null);
      })
      .catch(() => {
        // No prior saved calculation for this CF (typically a 404) — start blank, not an error.
        setAnswers({});
        setOutputLines([]);
        setCalculatedAt(null);
      })
      .finally(() => setResultLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfId]);

  // Once the field config arrives, fill in any still-blank answer with its configured default
  // (DROPDOWN's isDefault option / NUMBER's defaultValue) — but never override a value already
  // present from a prior saved calculation.
  useEffect(() => {
    if (!fields.length) return;
    setAnswers((prev) => ({ ...defaultCapexAnswers(fields), ...prev }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields]);

  const runCalculation = () => {
    const validationError = validateCapexInputs(fields, answers);
    if (validationError) {
      setShowErrors(true);
      toast.error(validationError);
      return Promise.reject(validationError);
    }
    const payload = {
      cfId,
      storeFormat: answers.STORE_FORMAT || "",
      inputAnswers: stripHiddenAnswers(fields, answers),
    };

    setSaving(true);
    return invokeApi(HTTP_METHODS.POST, `${CF_APPROVAL_HOSTNAME}${REST_URLS.CAPEX_CALCULATE}`, payload)
      .then((response) => {
        setOutputLines(response?.outputLines || []);
        setCalculatedAt(response?.calculatedAt || new Date().toISOString());
        toast.success("Capex Calculator saved");
      })
      .catch((err) => {
        toast.error(err?.message || "Failed to calculate Capex");
        throw err;
      })
      .finally(() => setSaving(false));
  };

  const handleSave = () => {
    runCalculation().catch(() => {});
  };

  // Draft-only calculation: hits /preview (calculateCapex without the CapexCalculation write),
  // so the Output card can show a live draft of the current, still-unsaved Input Form answers.
  const runPreview = () => {
    const validationError = validateCapexInputs(fields, answers);
    if (validationError) {
      setShowErrors(true);
      toast.error(validationError);
      return Promise.reject(validationError);
    }
    const payload = {
      storeFormat: answers.STORE_FORMAT || "",
      inputAnswers: stripHiddenAnswers(fields, answers),
    };

    setPreviewing(true);
    return invokeApi(HTTP_METHODS.POST, `${CF_APPROVAL_HOSTNAME}${REST_URLS.CAPEX_PREVIEW}`, payload)
      .then((response) => {
        setOutputLines(response?.outputLines || []);
        toast.success("Preview generated (not saved)");
      })
      .catch((err) => {
        toast.error(err?.message || "Failed to preview Capex");
        throw err;
      })
      .finally(() => setPreviewing(false));
  };

  const handlePreview = () => {
    runPreview().catch(() => {});
  };

  const handleSendCapex = () => {
    setSendingCapex(true);
    invokeApi(HTTP_METHODS.POST, `${CF_APPROVAL_HOSTNAME}${REST_URLS.CAPEX_RESULT}${cfId}/notify-creator`)
      .then((response) => toast.success(`Capex notification sent to ${response?.sentTo || "the CF creator"}`))
      .catch((err) => toast.error(err?.message || "Failed to send Capex notification"))
      .finally(() => setSendingCapex(false));
  };

  const handleSaveAndDownload = () => {
    runCalculation()
      .then(() => {
        setDownloading(true);
        return downloadCapexExport(cfId);
      })
      .then(() => toast.success("Capex Calculator workbook downloaded"))
      .catch((err) => {
        if (err) toast.error(err?.message || "Failed to download Capex Calculator workbook");
      })
      .finally(() => setDownloading(false));
  };

  const loading = fieldsLoading || resultLoading;

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton
            size="small"
            onClick={() => navigate(`${CF_APPROVAL_COMMON_ROUTE_V2}/form-details/${cfId}`)}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" fontWeight={700}>
            Capex Calculator
            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              #{cfId}
            </Typography>
          </Typography>
        </Stack>
        {calculatedAt && (
          <Typography variant="caption" color="text.secondary">
            Last calculated {moment(calculatedAt).format("DD MMM YYYY, hh:mm A")}
          </Typography>
        )}
      </Stack>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", pt: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={3}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography
              variant="subtitle2"
              sx={{ color: "#008938", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, mb: 2 }}
            >
              Input Form
            </Typography>
            <CapexInputForm
              fields={fields}
              answers={answers}
              onChange={(fieldId, value) =>
                setAnswers((prev) => ({ ...prev, [fieldId]: value }))
              }
              showErrors={showErrors}
            />
            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
              <Tooltip title={canUseCapexActions ? "" : NO_PERMISSION_MESSAGE}>
                <span>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={previewing ? <CircularProgress size={14} /> : <VisibilityIcon fontSize="small" />}
                    disabled={!canUseCapexActions || saving || downloading || previewing || loading}
                    onClick={handlePreview}
                  >
                    Preview
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
              <Typography
                variant="subtitle2"
                sx={{ color: "#008938", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}
              >
                Output
              </Typography>
              <Stack direction="row" spacing={1}>
                <Tooltip title={canUseCapexActions ? "" : NO_PERMISSION_MESSAGE}>
                  <span>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={saving ? <CircularProgress size={14} /> : <SaveIcon fontSize="small" />}
                      disabled={!canUseCapexActions || saving || downloading || previewing || loading || true}
                      onClick={handleSave}
                    >
                      Save
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title={canUseCapexActions ? "" : NO_PERMISSION_MESSAGE}>
                  <span>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={downloading ? <CircularProgress size={14} /> : <DownloadIcon fontSize="small" />}
                      disabled={!canUseCapexActions || saving || downloading || previewing || loading || true}
                      onClick={handleSaveAndDownload}
                    >
                      Save and Download
                    </Button>
                  </span>
                </Tooltip>
                {isStep18User && (
                  <Tooltip title={canSendCapex ? "" : "Save the Capex Calculator before sending"}>
                    <span>
                      <Button
                        variant="outlined"
                        size="small"
                        color="success"
                        startIcon={sendingCapex ? <CircularProgress size={14} /> : <SendIcon fontSize="small" />}
                        disabled={!canSendCapex || saving || downloading || previewing || sendingCapex || loading || true}
                        onClick={handleSendCapex}
                      >
                        Send Capex
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </Stack>
            </Stack>
            <CapexOutput outputLines={outputLines} />
          </Paper>
        </Stack>
      )}
    </Box>
  );
};

export default CapexCalculatorPage;
